import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Timer, X } from 'lucide-react';

// Play chime function using Web Audio API
const playChime = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create multiple beeps for a more noticeable chime
    const playBeep = (startTime, frequency, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.4, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const now = audioContext.currentTime;
    // Play a pleasant chime sequence
    playBeep(now, 523.25, 0.2);        // C5
    playBeep(now + 0.12, 659.25, 0.2); // E5
    playBeep(now + 0.24, 783.99, 0.4); // G5
    
  } catch (error) {
    console.log('Audio not supported:', error);
    // Fallback: try to vibrate if available
    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200]);
    }
  }
};

const RestTimer = ({ onClose, onComplete }) => {
  const [duration, setDuration] = useState(null); // 30 or 60 seconds
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    let interval;
    if (isRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            // Play chime when timer ends
            playChime();
            onComplete?.();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, timeLeft, onComplete]);

  const startTimer = (seconds) => {
    setDuration(seconds);
    setTimeLeft(seconds);
    setIsRunning(true);
  };

  const pauseTimer = () => {
    setIsRunning(false);
  };

  const resumeTimer = () => {
    if (timeLeft > 0) {
      setIsRunning(true);
    }
  };

  const resetTimer = () => {
    setTimeLeft(duration);
    setIsRunning(false);
  };

  const skipTimer = () => {
    setIsRunning(false);
    setTimeLeft(0);
    onComplete?.();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    if (!duration) return 0;
    return ((duration - timeLeft) / duration) * 100;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="rest-timer-overlay">
      <Card className="glass p-8 max-w-md w-full relative animate-fade-in">
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="absolute top-4 right-4 text-blue-300 hover:text-white"
          data-testid="close-timer-btn"
        >
          <X className="w-5 h-5" />
        </Button>

        <div className="text-center space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 mb-4">
            <Timer className="w-8 h-8 text-white" />
          </div>

          <h2 className="text-2xl font-bold text-white" data-testid="rest-timer-title">Rest Timer</h2>

          {!duration ? (
            <div className="space-y-4">
              <p className="text-blue-200">Select rest duration:</p>
              <div className="grid grid-cols-2 gap-4">
                <Button
                  onClick={() => startTimer(30)}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold py-8 rounded-xl btn-primary"
                  data-testid="timer-30s-btn"
                >
                  <div className="text-center">
                    <div className="text-3xl font-bold">30</div>
                    <div className="text-sm">seconds</div>
                  </div>
                </Button>
                <Button
                  onClick={() => startTimer(60)}
                  className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold py-8 rounded-xl btn-primary"
                  data-testid="timer-60s-btn"
                >
                  <div className="text-center">
                    <div className="text-3xl font-bold">60</div>
                    <div className="text-sm">seconds</div>
                  </div>
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Circular Progress */}
              <div className="relative w-48 h-48 mx-auto">
                <svg className="w-48 h-48 transform -rotate-90">
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    stroke="url(#gradient)"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 88}`}
                    strokeDashoffset={`${2 * Math.PI * 88 * (1 - getProgressPercentage() / 100)}`}
                    strokeLinecap="round"
                    className="transition-all duration-1000 ease-linear"
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3b82f6" />
                      <stop offset="100%" stopColor="#8b5cf6" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-white" data-testid="timer-display">
                      {formatTime(timeLeft)}
                    </div>
                    <div className="text-blue-300 text-sm mt-2">
                      {isRunning ? 'Resting...' : timeLeft === 0 ? 'Complete!' : 'Paused'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="flex gap-3 justify-center">
                {timeLeft > 0 && (
                  <>
                    {isRunning ? (
                      <Button
                        onClick={pauseTimer}
                        variant="outline"
                        className="glass border-yellow-400 text-yellow-300 hover:bg-yellow-500/20"
                        data-testid="pause-timer-btn"
                      >
                        Pause
                      </Button>
                    ) : (
                      <Button
                        onClick={resumeTimer}
                        className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                        data-testid="resume-timer-btn"
                      >
                        Resume
                      </Button>
                    )}
                    <Button
                      onClick={resetTimer}
                      variant="outline"
                      className="glass border-blue-400 text-blue-300 hover:bg-blue-500/20"
                      data-testid="reset-timer-btn"
                    >
                      Reset
                    </Button>
                  </>
                )}
                <Button
                  onClick={skipTimer}
                  variant="outline"
                  className="glass border-purple-400 text-purple-300 hover:bg-purple-500/20"
                  data-testid="skip-timer-btn"
                >
                  {timeLeft === 0 ? 'Done' : 'Skip'}
                </Button>
              </div>

              {timeLeft === 0 && (
                <div className="text-center">
                  <p className="text-emerald-400 font-semibold text-lg">Rest complete! Ready for next set 💪</p>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default RestTimer;
